import type Phaser from "phaser";
import { BUILDING_IDS, DECORATION_IDS } from "@engine";
import {
  boardBackArt,
  boardFrontArt,
  buildingArt,
  checkBubbleArt,
  crystalBubbleArt,
  decorationArt,
  fireArt,
  goblinArt,
  goldBubbleArt,
  harbourArt,
  rubbleArt,
  sackArt,
  smokeArt,
  surroundArt,
  type Art,
} from "../../art/design/village.ts";

/** Texture pixels per design unit, per layer. */
const RES = {
  surround: 1.25,
  board: 3,
  sprite: 4,
} as const;

export type TextureInfo = { key: string; art: Art; res: number };

export const TEXTURES = new Map<string, TextureInfo>();

export function svgUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function rasterise(svg: string, width: number, height: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(width);
      canvas.height = Math.ceil(height);
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("2D canvas unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas);
    };
    img.onerror = () => reject(new Error("Could not draw village art"));
    img.src = svgUrl(svg);
  });
}

async function add(scene: Phaser.Scene, key: string, art: Art, res: number): Promise<void> {
  const canvas = await rasterise(art.svg, art.box.w * res, art.box.h * res);
  if (scene.textures.exists(key)) {
    scene.textures.remove(key);
  }
  scene.textures.addCanvas(key, canvas);
  TEXTURES.set(key, { key, art, res });
}

/** Draws every piece of design art into Phaser textures. */
export async function registerDesignTextures(scene: Phaser.Scene): Promise<void> {
  const jobs: Array<Promise<void>> = [
    add(scene, "surround", surroundArt(), RES.surround),
    add(scene, "board-back", boardBackArt(), RES.board),
    add(scene, "board-front", boardFrontArt(), RES.board),
    add(scene, "harbour", harbourArt(), RES.sprite),
    add(scene, "bubble-gold", goldBubbleArt(), RES.sprite),
    add(scene, "bubble-crystal", crystalBubbleArt(), RES.sprite),
    add(scene, "bubble-check", checkBubbleArt(), RES.sprite),
    add(scene, "raid-goblin", goblinArt(), RES.sprite),
    add(scene, "raid-sack", sackArt(), RES.sprite),
    add(scene, "raid-smoke", smokeArt(), RES.sprite),
    add(scene, "raid-fire", fireArt(), RES.sprite),
    add(scene, "raid-rubble", rubbleArt(), RES.sprite),
  ];
  for (const id of BUILDING_IDS) {
    const art = buildingArt(id);
    if (art) {
      jobs.push(add(scene, `b-${id}`, art, RES.sprite));
    }
  }
  for (const id of DECORATION_IDS) {
    jobs.push(add(scene, `d-${id}`, decorationArt(id), RES.sprite));
  }
  await Promise.all(jobs);
}

/**
 * Places a design-art image so its art origin (0,0) sits at (x, y) in board units.
 */
export function placeArt(
  scene: Phaser.Scene,
  key: string,
  x: number,
  y: number,
  scale = 1,
): Phaser.GameObjects.Image {
  const info = TEXTURES.get(key);
  const image = scene.add.image(x, y, key);
  if (!info) {
    return image;
  }
  const { box } = info.art;
  image.setOrigin(-box.x / box.w, -box.y / box.h);
  image.setScale(scale / info.res);
  return image;
}
